import { HubSearchResult } from '../../types/hubSearch';
import {
  CRMUser,
  EnhancedDeal,
  ProjectTask,
  TaskComment,
  TaskGroup,
  TimberProject,
} from '../../types';
import { buildSearchableText, scoreMultiFieldMatch } from '../search';

export interface SearchProjectHubOptions {
  query: string;
  timberProjects: TimberProject[];
  projectTasks: ProjectTask[];
  taskGroups: TaskGroup[];
  taskComments: TaskComment[];
  enhancedDeals: EnhancedDeal[];
  crmUsers: CRMUser[];
  currentProjectId?: string | null;
  limit?: number;
}

const CURRENT_PROJECT_BOOST = 0.15;
const SECTION_ORDER: Record<string, number> = {
  Projects: 0,
  Tasks: 1,
  Groups: 2,
  Comments: 3,
};

function userName(uid: string | undefined, crmUsers: CRMUser[]): string {
  if (!uid) return '';
  return crmUsers.find(u => u.uid === uid)?.displayName || '';
}

function dealForProject(project: TimberProject, deals: EnhancedDeal[]): EnhancedDeal | undefined {
  return deals.find(d => d.id === project.dealId);
}

function projectLabel(project: TimberProject, deal?: EnhancedDeal): string {
  const parts = [
    deal?.projectNumber ? `#${deal.projectNumber.replace(/^#/, '')}` : null,
    project.name,
    project.clientName,
  ].filter(Boolean);
  return parts.join(' · ');
}

function applyBoost(score: number, projectId: string | undefined, currentProjectId?: string | null): number {
  if (!currentProjectId || !projectId || projectId !== currentProjectId) return score;
  return score + CURRENT_PROJECT_BOOST;
}

function sortResults(results: HubSearchResult[]): HubSearchResult[] {
  return [...results].sort((a, b) => {
    if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
    return (SECTION_ORDER[a.section] ?? 99) - (SECTION_ORDER[b.section] ?? 99);
  });
}

export function searchProjectHub(options: SearchProjectHubOptions): HubSearchResult[] {
  const {
    query,
    timberProjects,
    projectTasks,
    taskGroups,
    taskComments,
    enhancedDeals,
    crmUsers,
    currentProjectId,
    limit = 30,
  } = options;

  const q = query.trim();
  if (!q) {
    const recent = [...timberProjects]
      .filter(p => p.id && p.status !== 'completed')
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
      .slice(0, 10);
    return recent.map(project => {
      const deal = dealForProject(project, enhancedDeals);
      return {
        id: project.id!,
        kind: 'project',
        section: 'Projects',
        title: project.name,
        subtitle: projectLabel(project, deal),
        matchScore: project.id === currentProjectId ? 1 + CURRENT_PROJECT_BOOST : 1,
        payload: project,
      };
    });
  }

  const projectById = new Map(timberProjects.filter(p => p.id).map(p => [p.id!, p]));
  const groupById = new Map(taskGroups.filter(g => g.id).map(g => [g.id!, g]));
  const taskById = new Map(projectTasks.filter(t => t.id).map(t => [t.id!, t]));
  const results: HubSearchResult[] = [];

  for (const project of timberProjects) {
    if (!project.id) continue;
    const deal = dealForProject(project, enhancedDeals);
    const pmName = userName(project.projectManager, crmUsers);
    const score = scoreMultiFieldMatch(q, [
      { text: project.name, weight: 10 },
      { text: project.clientName, weight: 8 },
      { text: project.location, weight: 6 },
      { text: project.description, weight: 4 },
      { text: deal?.descriptor, weight: 7 },
      { text: deal?.clientLabel, weight: 7 },
      { text: deal?.projectNumber, weight: 9 },
      { text: deal?.title, weight: 8 },
      { text: pmName, weight: 5 },
    ]);
    if (score <= 0) continue;
    results.push({
      id: project.id,
      kind: 'project',
      section: 'Projects',
      title: project.name,
      subtitle: [projectLabel(project, deal), project.location].filter(Boolean).join(' · '),
      matchScore: applyBoost(score, project.id, currentProjectId),
      payload: project,
    });
  }

  for (const task of projectTasks) {
    if (!task.id) continue;
    const project = projectById.get(task.projectId);
    const group = task.groupId ? groupById.get(task.groupId) : undefined;
    const assigneeNames = (task.assignedTo || []).map(uid => userName(uid, crmUsers)).join(' ');
    const score = scoreMultiFieldMatch(q, [
      { text: task.title, weight: 10 },
      { text: task.description, weight: 6 },
      { text: task.notes, weight: 4 },
      { text: (task.tags || []).join(' '), weight: 5 },
      { text: task.status, weight: 3 },
      { text: assigneeNames, weight: 4 },
      { text: group?.name, weight: 3 },
    ]);
    if (score <= 0) continue;
    results.push({
      id: task.id,
      kind: 'task',
      section: 'Tasks',
      title: task.title,
      subtitle: [project?.name, group?.name, task.status].filter(Boolean).join(' · '),
      matchScore: applyBoost(score, task.projectId, currentProjectId),
      payload: task,
    });
  }

  for (const group of taskGroups) {
    if (!group.id) continue;
    const project = projectById.get(group.projectId);
    const score = scoreMultiFieldMatch(q, [
      { text: group.name, weight: 10 },
      { text: project?.name, weight: 4 },
    ]);
    if (score <= 0) continue;
    results.push({
      id: group.id,
      kind: 'group',
      section: 'Groups',
      title: group.name,
      subtitle: project?.name || '',
      matchScore: applyBoost(score, group.projectId, currentProjectId),
      payload: group,
    });
  }

  for (const comment of taskComments) {
    if (!comment.id) continue;
    const task = comment.taskId ? taskById.get(comment.taskId) : undefined;
    const projectId = comment.projectId || task?.projectId;
    const project = projectId ? projectById.get(projectId) : undefined;
    const body = buildSearchableText(comment.bodyHtml || comment.body);
    const score = scoreMultiFieldMatch(q, [
      { text: body, weight: 8 },
      { text: comment.authorName, weight: 4 },
      { text: task?.title, weight: 5 },
    ]);
    if (score <= 0) continue;
    results.push({
      id: comment.id,
      kind: 'comment',
      section: 'Comments',
      title: task?.title ? `Comment on ${task.title}` : 'Comment',
      subtitle: [comment.authorName, project?.name, body.slice(0, 80)].filter(Boolean).join(' · '),
      matchScore: applyBoost(score, projectId, currentProjectId),
      payload: comment,
    });
  }

  return sortResults(results).slice(0, limit);
}
